const CSRF_COOKIE_NAME = 'ca_csrf'
const CSRF_HEADER_NAME = 'X-CSRF-TOKEN'
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

let csrfRequestToken: string | null | undefined

const escapeCookieName = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const getCsrfCookie = (): string | null => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapeCookieName(CSRF_COOKIE_NAME)}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
}

export const getCsrfToken = (): string | null => csrfRequestToken || null

export const setCsrfToken = (token?: string | null): string | null => {
    csrfRequestToken = token || null
    return csrfRequestToken
}

export const clearCsrfToken = (): void => {
    csrfRequestToken = null
}

export const readCsrfTokenFromResponse = (response?: Pick<Response, 'headers'> | null): string | null =>
    response?.headers.get(CSRF_HEADER_NAME) || null

export const isUnsafeMethod = (method?: string): boolean => UNSAFE_METHODS.has((method || 'GET').toUpperCase())

type HeaderRecord = Record<string, string | undefined>
type HeaderTupleList = string[][]
type HeaderInput = HeadersInit | HeaderTupleList | HeaderRecord | undefined

// Convert loose header input into a concrete Headers object while dropping undefined values.
const toHeaders = (headers: HeaderInput): Headers => {
    if (headers instanceof Headers) {
        return new Headers(headers)
    }

    if (Array.isArray(headers)) {
        const normalizedTuples = headers.filter((entry): entry is [string, string] => entry.length >= 2)
        return new Headers(normalizedTuples as [string, string][])
    }

    const normalizedHeaders = Object.entries(headers ?? {}).reduce<Record<string, string>>((acc, [key, value]) => {
        if (typeof value !== 'undefined') {
            acc[key] = value
        }

        return acc
    }, {})

    return new Headers(normalizedHeaders)
}

export const withCsrfHeader = (headers: HeaderInput, csrfToken: string): Headers => {
    const nextHeaders = toHeaders(headers)
    nextHeaders.set(CSRF_HEADER_NAME, csrfToken)
    return nextHeaders
}

export const ensureCsrfToken = async (
    handshake: () => Promise<string | null | undefined>,
    { forceRefresh = false }: { forceRefresh?: boolean } = {}
): Promise<string> => {
    if (!forceRefresh) {
        const existingToken = getCsrfToken()
        if (existingToken) return existingToken
    }

    const refreshedToken = await handshake()
    if (!refreshedToken) {
        throw new Error(`CSRF header "${CSRF_HEADER_NAME}" is missing after handshake`)
    }

    setCsrfToken(refreshedToken)
    return refreshedToken
}

export { CSRF_COOKIE_NAME, CSRF_HEADER_NAME }
